import { useRef, useEffect, useState, useCallback } from 'react';

interface UseShowWithTransitionArgs {
  delay: number;
  onHide: () => void;
}

/**
 * Hook which shows and hides elements with a delay to allow time for transitions
 * @param {number} delay time in ms for the hide transition
 * @param {Function} onHide function to hide an element
 * @return {boolean} response.isShow check if an element is show
 * @return {Function} response.hideWithTransition function to hide an element with delay
 */
const useShowWithTransition = ({ delay, onHide }: UseShowWithTransitionArgs) => {
  const timer = useRef<number>();
  const [isShow, setIsShow] = useState<boolean>(false);

  // show with delay on mount
  useEffect(() => {
    timer.current = window.setTimeout(() => {
      setIsShow(true);
      window.clearTimeout(timer.current);
      // the show can be done in a fixed time
    }, 100);

    return () => {
      window.clearTimeout(timer.current);
    };
  }, []);

  // hide with delay
  const hideWithTransition = useCallback(() => {
    setIsShow(false);

    timer.current = window.setTimeout(() => {
      onHide();
      window.clearTimeout(timer.current);
    }, delay);
  }, [onHide, delay]);

  return {
    isShow,
    hideWithTransition,
  };
};

export default useShowWithTransition;
